import { createSelector } from 'reselect';
import { RootState } from '@/models/root-state.model';
import { LimitDuration } from '@lucky7ventures/lucky-components';
import { BffUserActiveLimit, BffUserLimitsResponse, LimitType } from '@lucky7ventures/bff-types';

export const selectLimits = (state: RootState): BffUserLimitsResponse | null => state.limits.limits;
export const selectLimitsLoading = (state: RootState): boolean => state.limits.loading;
export const selectLimitsLoaded = (state: RootState): boolean => state.limits.loaded;
export const selectLimitsError = (state: RootState): boolean => state.limits.error;

export const selectActiveLimits = createSelector(selectLimits, limits =>
  limits ? limits.activeLimits.sort((a, b) => a.duration - b.duration) : [],
);

export const selectFutureLimits = createSelector(selectLimits, limits =>
  limits
    ? limits.futureLimits.filter(limit => !limit.endDate).sort((a, b) => a.duration - b.duration)
    : [],
);

export const selectActiveDepositLimits = createSelector(
  selectActiveLimits,
  limits => limits?.filter(limit => limit.limitType === LimitType.Deposit),
);

export const selectFutureDepositLimits = createSelector(
  selectFutureLimits,
  limits => limits?.filter(limit => limit.limitType === LimitType.Deposit),
);

export const selectActiveSessionLimits = createSelector(
  selectActiveLimits,
  limits => limits?.filter(limit => limit.limitType === LimitType.Session),
);

export const selectFutureSessionLimits = createSelector(
  selectFutureLimits,
  limits => limits?.filter(limit => limit.limitType === LimitType.Session),
);

export const selectActiveLossLimits = createSelector(
  selectActiveLimits,
  limits => limits?.filter(limit => limit.limitType === LimitType.CasinoNetLoss),
);

export const selectFutureLossLimits = createSelector(
  selectFutureLimits,
  limits => limits?.filter(limit => limit.limitType === LimitType.CasinoNetLoss),
);

export const selectActiveDepositLimit = createSelector(
  selectActiveDepositLimits,
  (state: RootState, duration: LimitDuration) => duration,
  (limits, duration): BffUserActiveLimit | undefined => {
    if (!limits) {
      return undefined;
    }

    return limits.find(l => l.duration === duration);
  },
);

export const selectLowestRemainingDepositLimit = createSelector(
  selectActiveDepositLimits,
  (activeLimits): number | null => {
    return activeLimits.reduce((lowestLimit: number | null, limit) => {
      const remainingAmount = limit.remainingAmount > 0 ? limit.remainingAmount : 0;

      if (lowestLimit === null) {
        return remainingAmount;
      }

      if (lowestLimit > remainingAmount) {
        return remainingAmount;
      }

      return lowestLimit;
    }, null);
  },
);
